import { Measurements } from '~/utility/BinSentry-ui-utility';
import { LitEntity, html, css } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import { bannerStyles } from '../../styles/banner-styles';
import '../../common/bn-map';
import '../../common/bn-map-marker';

class SchedulerMap extends LitEntity {
  constructor() {
    super();
    this.locations = [];
    this.farms = [];
  }

  static get properties() {
    return {
      locations: { type: Array },
      farms: { type: Array },
      routeSummary: { type: Object },
      routeClass: { type: String },
    };
  }

  static get styles() {
    return [sharedStyles, bannerStyles, css`
      :host {
        display: block;
        min-height: 400px;
      }
      bn-map {
        height: 95vh;
        width: 100%;
      }
      .route-summary {
        position: absolute;
        bottom: 1rem;
        left: 1rem;
        font-size: 1.2rem;
      }
      .total {
        margin-top: .5rem;
        font-weight: bold;
      }
      .total label {
        font-weight: bold;
      }
    `];
  }

  render() {
    return html`
      <bn-map directions map-type="roadmap" @routeresponse="${this._onRouteResponse}">
        ${this.farms.map(farm => html`
          <bn-map-marker
            latitude="${farm.properties.latitude}"
            longitude="${farm.properties.longitude}"
          >
            ${farm.properties.name}
          </bn-map-marker>
        `)}
      </bn-map>
      ${this._renderSummary()}
    `;
  }

  _renderSummary() {
    if (!this.routeSummary) {
      return html``;
    }

    const { binCount, distance, driveTime, workTime, totalTime } = this.routeSummary;

    const km = distance / 1000;
    const measurement = Measurements.get('distance');
    return html`
      <div class="route-summary banner banner--${this.routeClass}">
        <div>
          ${binCount}
          <label>bins</label>
        </div>
        <div>
          ${measurement.html({ distance: km })}
        </div>
        <div>
          <label>Driving Time:</label>
          ${this._renderDuration(driveTime)}
        </div>
        <div>
          <label>Working Time:</label>
          ${this._renderDuration(workTime)}
        </div>
        <div class="total">
          <label>Total Time:</label>
          ${this._renderDuration(totalTime)}
        </div>
      </div>
    `;
  }

  _renderDuration(durationInSeconds) {
    const seconds = durationInSeconds % 60;
    const remainingDurationInMinutes = (durationInSeconds - seconds) / 60;
    const minutes = `${remainingDurationInMinutes % 60}`.padStart(2, '0');
    const hours = (remainingDurationInMinutes - minutes) / 60;
    return html`${hours}<span class="unit">h</span> ${minutes}<span class="unit">m</span>`;
  }

  update(changedProperties) {
    if (changedProperties.has('locations')) {
      this.routeSummary = undefined;
      this._getFarms();
    }
    super.update(changedProperties);
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('farms')) {
      this.shadowRoot.querySelector('bn-map').updateSlottedMarkers();
    }
  }

  async _getFarms() {
    if (!this.locations?.length) {
      this.farms = [];
      return;
    }
    const farms = await Promise.all(this.locations.map(location => this.fetchSiren(location.getSubEntityByRel('https://api.binsentry.com/rel/farm').href)));
    this.farms = farms.filter(f => f?.properties);
  }

  _onRouteResponse(e) {
    const { response } = e.detail;

    const route = response.routes[0];

    let distance = 0; // meters
    let driveTime = 0; // seconds

    for (const leg of route.legs) {
      distance += leg.distance.value;
      driveTime += leg.duration.value;
    }

    const binCount = this.locations.reduce((a,v) => a += v.properties.workOrderCount, 0);
    const workTime = binCount * 900; // 15 minutes per bin

    const totalTime = workTime + driveTime;

    const lessThanEightHoursOfWork = totalTime < 28800;
    const moreThanNineHoursOfWork = totalTime > 32400;

    if (lessThanEightHoursOfWork) {
      this.routeClass = 'success';
    } else if (moreThanNineHoursOfWork) {
      this.routeClass = 'critical';
    } else {
      this.routeClass = 'warn';
    }

    this.routeSummary = {
      binCount,
      distance,
      driveTime,
      workTime,
      totalTime,
    };
  }
}

customElements.define('scheduler-map', SchedulerMap);
